import {
  useUpdateCompany,
  useUpdateUser,
} from "@/hooks/queries/useAuthQueries";
import { useLoggedInCompany } from "@/hooks/queries/useCompanyQueries";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import ProfileHeader from "@/components/profile/ProfileHeader";
import ProfileTabs from "@/components/profile/ProfileTabs";
import { getAuthToken } from "@/utils/secureStorage";
import { API_BASE_URL } from "@/lib/axios";

const Profile = () => {
  const { t } = useTranslation();
  const { user, currentUserLoading, isInitialized, updateAuthState } = useAuth();
  const updateUserMutation = useUpdateUser();
  const updateCompanyMutation = useUpdateCompany();

  // Get company data for company admins
  const {
    data: companyData,
    isLoading: companyLoading,
    error: companyError,
    refetch: refetchCompanyData,
  } = useLoggedInCompany(user?.role === "company_admin");

  // Ensure company data is fetched for company admins
  useEffect(() => {
    if (user?.role === "company_admin") {
      // If no company data and not loading, try to refetch
      if (!companyData && !companyLoading && !companyError) {
        refetchCompanyData();
      }
    }
  }, [
    user?.role,
    companyData,
    companyLoading,
    companyError,
    refetchCompanyData,
  ]);
  
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    photo: "",
    logo: "",
  });

  const [companyName, setCompanyName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get full image URL
  const getImageUrl = (photo: string) => {
    if (!photo) return "";
    if (photo.startsWith('data:')) {
      return photo;
    }
    if (photo.startsWith('http')) {
      return photo;
    }
    // Your backend serves images at /images/ path
    return `${photo}`;
  };

  // Check for edit parameter in URL and set editing mode
  useEffect(() => {
    const editParam = searchParams.get("edit");
    if (editParam) {
      setIsEditing(true);
    }
  }, [searchParams]);

  // Update form data when user or company data changes
  useEffect(() => {
    if (user) {

      if (user.role === "company_admin" && companyData?.data?.document) {
        // For company admins, use backend company data
        const company = companyData.data.document;
        setFormData({
          fullName: company.name || "",
          email: company.primaryEmail || "",
          phoneNumber: company.phoneNumber || "",
          photo: company.logo || "",
          logo: company.logo || "",
        });
      } else if (user.role === "company_admin") {
        // For company admins without company data yet, use user data as fallback
        setFormData({
          fullName: user.name || "",
          email: user.primaryEmail || "",
          phoneNumber: user.phoneNumber || "",
          photo: "", // Company admins don't have user photos
          logo: "", // Company admins don't have user logos
        });
      } else {
        // For regular users, use user data
        setFormData({
          fullName: user.fullName || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
          photo: user.photo || "",
          logo: "", // Regular users don't have logos
        });
      }
    }
  }, [user, companyData]);

  // Set company name from backend data when available
  useEffect(() => {
    if (user?.role === "company_admin" && companyData?.data?.document) {
      setCompanyName(companyData.data.document.name);
    } else if (user?.companyId) {
      // Fallback to fetching company name for non-company-admin users
      const fetchCompanyName = async () => {
        try {
          const token = getAuthToken();
          if (!token) {
            console.error(t("profile.noTokenFound"));
            return;
          }

          const response = await fetch(
            `${import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com"}/api/v1/companies/${user.companyId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          if (data.status === "success" && data.data?.name) {
            setCompanyName(data.data.name);
          } else {
            console.error(t("profile.invalidResponseFormat"), data);
          }
        } catch (error) {
          console.error(t("profile.failedToFetchCompanyName"), error);
          setCompanyName(t("common.error"));
        }
      };

      fetchCompanyName();
    }
  }, [user?.companyId, user?.role, companyData, t]);

  // Handle file change with size validation and compression
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error(t("profile.fileTooLarge", "File size must be less than 2MB"));
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t("profile.invalidFileType", "Please select a valid image file"));
      return;
    }

    // Compress and resize image
    const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.8): Promise<string> => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx?.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        
        img.src = URL.createObjectURL(file);
      });
    };

    // Compress the image
    compressImage(file)
      .then((compressedDataUrl) => {
          if (user?.role === "company_admin") {
          setFormData((prev) => ({ ...prev, logo: compressedDataUrl }));
          } else {
          setFormData((prev) => ({ ...prev, photo: compressedDataUrl }));
        }
        toast.success(t("profile.imageCompressed", "Image compressed and ready to upload"));
      })
      .catch((error) => {
        console.error(t("profile.errorCompressingImage"), error);
        toast.error(t("profile.imageCompressionError", "Error processing image"));
      });
  };

  // Convert base64 to blob for FormData
  const dataURLtoBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (user?.role === "company_admin") {
        // Update company data - match backend API expectations
        const companyFormData = new FormData();
        companyFormData.append("name", formData.fullName);
        
        // Convert base64 to blob if logo exists
        if (formData.logo && formData.logo.startsWith('data:')) {
          const logoBlob = dataURLtoBlob(formData.logo);
          companyFormData.append("logo", logoBlob, "logo.jpg");
        }
        
        const result = await updateCompanyMutation.mutateAsync(companyFormData);
        toast.success(t("profile.companyUpdatedSuccessfully"));
        
        // Refetch company data to get updated information
        refetchCompanyData();
        
        // Update formData with the new logo filename from backend
        if (result?.data?.document?.logo) {
          setFormData(prev => ({ ...prev, logo: result.data.document.logo }));
        }
      } else {
        // Update user data - match backend API expectations
        const userFormData = new FormData();
        userFormData.append("fullName", formData.fullName);
        
        // Convert base64 to blob if photo exists
        if (formData.photo && formData.photo.startsWith('data:')) {
          const photoBlob = dataURLtoBlob(formData.photo);
          userFormData.append("photo", photoBlob, "photo.jpg");
        }
        
        const result = await updateUserMutation.mutateAsync(userFormData);
        
        // Update formData with the new photo filename from backend
        if (result?.data?.document?.photo) {
          setFormData(prev => ({ ...prev, photo: result.data.document.photo }));
        }
        
        // Update auth state with new user data
        if (result?.data?.document) {
          // Merge the updated user data with current user data
          const updatedUser = { ...user, ...result.data.document };
          updateAuthState(updatedUser);
        }
        toast.success(t("profile.profileUpdatedSuccessfully"));
      }

      setIsEditing(false);
      } catch (error) {
      console.error(t("profile.errorUpdatingProfile"), error);
      toast.error(t("profile.updateFailed"));
    }
  };

  // Loading state
  if (currentUserLoading || !isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="h-16 w-16 animate-pulse-slow rounded-full bg-primary-500"></div>
          <h1 className="text-2xl font-bold text-primary-800 dark:text-primary-200">
            {t("profile.loading")}
          </h1>
        </div>
      </div>
    );
  }

  // User not found state
  if (!user) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500 dark:text-gray-400">
          {t("profile.userNotFound")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <ProfileHeader 
          user={user} 
          companyData={companyData?.data?.document} 
          isCompanyAdmin={user?.role === "company_admin"} 
        />
        
        <ProfileTabs
          user={user}
          companyData={companyData?.data?.document}
          isEditing={isEditing}
          formData={formData}
          companyName={companyName}
          fileInputRef={fileInputRef}
          getImageUrl={getImageUrl}
          onEditClick={() => setIsEditing(true)}
          onCancelEdit={() => {
            setIsEditing(false);
            // Reset form data to current user/company data
            if (user) {
              if (user.role === "company_admin" && companyData?.data?.document) {
                const company = companyData.data.document;
                setFormData({
                  fullName: company.name || "",
                  email: company.primaryEmail || "",
                  phoneNumber: company.phoneNumber || "",
                  photo: company.logo || "",
                  logo: company.logo || "",
                });
              } else {
                setFormData({
                  fullName: user.fullName || "",
                  email: user.email || "",
                  phoneNumber: user.phoneNumber || "",
                  photo: user.photo || "",
                  logo: "",
                });
              }
            }
          }}
          onFormDataChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
          onCompanyNameChange={setCompanyName}
          onFileChange={handleFileChange}
          onFileInputClick={() => fileInputRef.current?.click()}
          onSubmit={handleSubmit}
          isSubmitting={updateUserMutation.isPending || updateCompanyMutation.isPending}
        />
      </div>
    </div>
  );
};

export default Profile;
